import Icon, { IconType } from 'components/Icon';
import { Tooltip } from 'components/Widget/Tooltip';
import { useId } from 'react';
import * as S from './styles';

interface PillProps {
	label: string;
	variant?: 'default' | 'deleted' | 'muted';
	onClick?: (e: React.MouseEvent) => void;
	onRemove?: () => void;
	tooltip?: string;
	iconLeft?: IconType;
	iconLeftColor?: string;
	// when true, the label is allowed to grow without the default 140px ellipsis cap —
	// useful for chip-like uses (emails, etc.) where truncation feels aggressive
	noTruncate?: boolean;
}

const Pill = ({
	label,
	variant = 'default',
	onClick,
	onRemove,
	tooltip,
	iconLeft,
	iconLeftColor,
	noTruncate,
}: PillProps) => {
	const tooltipId = useId();
	const tooltipProps = tooltip
		? { 'data-tooltip-id': tooltipId, 'data-tooltip-content': tooltip }
		: {};
	const leadingIcon =
		iconLeft !== undefined ? (
			<S.IconContainer>
				<Icon type={iconLeft} color={iconLeftColor} width={16} height={16} />
			</S.IconContainer>
		) : null;

	// When onRemove is present, always use split layout (two sibling elements)
	if (onRemove) {
		const clickable = !!onClick;
		return (
			<S.SplitWrapper>
				<S.SplitLabel
					as={clickable ? 'button' : 'span'}
					$variant={variant}
					$clickable={clickable}
					onClick={onClick}
					type={clickable ? 'button' : undefined}
					{...tooltipProps}
				>
					{leadingIcon}
					<S.Label $noTruncate={noTruncate}>{label}</S.Label>
				</S.SplitLabel>
				<S.SplitRemove $variant={variant} type="button" onClick={onRemove} aria-label="Remove">
					<Icon type={IconType.CLOSE} width={10} height={10} />
				</S.SplitRemove>
				{tooltip && <Tooltip id={tooltipId} placement="top" />}
			</S.SplitWrapper>
		);
	}

	const clickable = !!onClick;
	const Wrapper = clickable ? 'button' : 'span';

	return (
		<>
			<S.Base
				as={Wrapper}
				$variant={variant}
				$clickable={clickable}
				onClick={onClick}
				type={clickable ? 'button' : undefined}
				{...tooltipProps}
			>
				{leadingIcon}
				<S.Label $noTruncate={noTruncate}>{label}</S.Label>
			</S.Base>
			{tooltip && <Tooltip id={tooltipId} placement="top" />}
		</>
	);
};

export default Pill;
